import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetRfidBadgeOrdersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/rfid-badge-orders/get-rfid-badge-orders-paginated.model';
import { BackOfficeRfidBadgeOrderModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/outputs/rfid-badge-orders/back-office-rfid-badge-order.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { EmspBoRfidBadgeOrderStatusFilterStateController } from '../emsp-bo-rfid-badge-order-status-filter-state/emsp-bo-rfid-badge-order-status-filter-state.controller';
import { EmspBoRfidBadgeOrderListBillingAddressFullNameColumn } from './emsp-bo-rfid-badge-order-list-billing-address-full-name.column';
import { EmspBoRfidBadgeOrderListConfirmedAtColumn } from './emsp-bo-rfid-badge-order-list-confirmed-at.column';
import { EmspBoRfidBadgeOrderListEmspUserDidColumn } from './emsp-bo-rfid-badge-order-list-emsp-user-did.column';
import { EmspBoRfidBadgeOrderListEmspUserEmailAddressColumn } from './emsp-bo-rfid-badge-order-list-emsp-user-email-address.column';
import { EmspBoRfidBadgeOrderListEmspUserFirstNameColumn } from './emsp-bo-rfid-badge-order-list-emsp-user-first-name.column';
import { EmspBoRfidBadgeOrderListEmspUserIdColumn } from './emsp-bo-rfid-badge-order-list-emsp-user-id.column';
import { EmspBoRfidBadgeOrderListEmspUserLastNameColumn } from './emsp-bo-rfid-badge-order-list-emsp-user-last-name.column';
import { EmspBoRfidBadgeOrderListOrderNumberColumn } from './emsp-bo-rfid-badge-order-list-order-number.column';
import { EmspBoRfidBadgeOrderListPaidAtColumn } from './emsp-bo-rfid-badge-order-list-paid-at.column';
import { EmspBoRfidBadgeOrderListQuantityColumn } from './emsp-bo-rfid-badge-order-list-quantity.column';
import { EmspBoRfidBadgeOrderListRfidBadgeVisualNumbersColumn } from './emsp-bo-rfid-badge-order-list-rfid-badge-visual-numbers.column';
import { EmspBoRfidBadgeOrderListShippedAtColumn } from './emsp-bo-rfid-badge-order-list-shipped-at.column';
import { EmspBoRfidBadgeOrderListStatusColumn } from './emsp-bo-rfid-badge-order-list-status.column';
import { EmspBoRfidBadgeOrderListTotalTtcColumn } from './emsp-bo-rfid-badge-order-list-total-ttc.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const EMSP_BO_RFID_BADGE_ORDER_LIST_SORT_FIELD: NonNullable<
  GetRfidBadgeOrdersPaginatedModel['sortField']
> = 'createdAt';

export const EMSP_BO_RFID_BADGE_ORDER_LIST_SORT_ORDER: NonNullable<
  GetRfidBadgeOrdersPaginatedModel['sortOrder']
> = -1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetRfidBadgeOrdersPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const EMSP_BO_RFID_BADGE_ORDER_LIST_FILTER: ResourceListFilter = {
  status: {
    componentType: EmspBoRfidBadgeOrderStatusFilterStateController,
  },
} as const;

export const EMSP_BO_RFID_BADGE_ORDER_LIST_FILTER_NAME_LIST = Object.keys(
  EMSP_BO_RFID_BADGE_ORDER_LIST_FILTER,
) as Array<keyof typeof EMSP_BO_RFID_BADGE_ORDER_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const emspBoRfidBadgeOrderListColumnHelper =
  createColumnHelper<BackOfficeRfidBadgeOrderModel>();

type ResourceColumnType = ColumnDef<BackOfficeRfidBadgeOrderModel, any>[];

export const EMSP_BO_RFID_BADGE_ORDER_LIST_COLUMN_LIST: ResourceColumnType = [
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'emspUser.id',
    EmspBoRfidBadgeOrderListEmspUserIdColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'emspUser.did',
    EmspBoRfidBadgeOrderListEmspUserDidColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'emspUser.firstName',
    EmspBoRfidBadgeOrderListEmspUserFirstNameColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'emspUser.lastName',
    EmspBoRfidBadgeOrderListEmspUserLastNameColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'emspUser.emailAddress',
    EmspBoRfidBadgeOrderListEmspUserEmailAddressColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'orderNumber',
    EmspBoRfidBadgeOrderListOrderNumberColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'rfidBadgeVisualNumbers',
    EmspBoRfidBadgeOrderListRfidBadgeVisualNumbersColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'quantity',
    EmspBoRfidBadgeOrderListQuantityColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'status',
    EmspBoRfidBadgeOrderListStatusColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'totalTTC',
    EmspBoRfidBadgeOrderListTotalTtcColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'confirmedAt',
    EmspBoRfidBadgeOrderListConfirmedAtColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'paidAt',
    EmspBoRfidBadgeOrderListPaidAtColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'shippedAt',
    EmspBoRfidBadgeOrderListShippedAtColumn,
  ),
  emspBoRfidBadgeOrderListColumnHelper.accessor(
    'billingAddress',
    EmspBoRfidBadgeOrderListBillingAddressFullNameColumn,
  ),
];

export const EMSP_BO_RFID_BADGE_ORDER_LIST_COLUMN_ID_LIST =
  EMSP_BO_RFID_BADGE_ORDER_LIST_COLUMN_LIST.flatMap((columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  });

export const EMSP_BO_RFID_BADGE_ORDER_LIST_COLUMN_ORDER = [
  ...EMSP_BO_RFID_BADGE_ORDER_LIST_COLUMN_ID_LIST,
];

export const EMSP_BO_RFID_BADGE_ORDER_LIST_COLUMN_VISIBILITY: VisibilityState =
  EMSP_BO_RFID_BADGE_ORDER_LIST_COLUMN_ID_LIST.reduce(
    (acc, id) => ({ ...acc, [id]: true }),
    {} as VisibilityState,
  );

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface EmspBoRfidBadgeOrderListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const EMSP_BO_RFID_BADGE_ORDER_LIST_DEFAULT_OPTIONS: EmspBoRfidBadgeOrderListOptionsModel =
  {
    columnVisibility: EMSP_BO_RFID_BADGE_ORDER_LIST_COLUMN_VISIBILITY,
    columnOrder: EMSP_BO_RFID_BADGE_ORDER_LIST_COLUMN_ORDER,
  };

export const EMSP_BO_RFID_BADGE_ORDER_LIST_COLUMN_LABEL_I18N_KEY: Record<
  string,
  string
> = {
  emspUserId: 'delmonicos-shared-sdk.outputs-emsp-user.id',
  emspUserDid: 'delmonicos-shared-sdk.outputs-emsp-user.did',
  emspUserFirstName: 'delmonicos-shared-sdk.outputs-emsp-user.first-name',
  emspUserLastName: 'delmonicos-shared-sdk.outputs-emsp-user.last-name',
  emspUserEmailAddress: 'delmonicos-shared-sdk.outputs-emsp-user.email-address',
  orderNumber: 'delmonicos-shared-sdk.outputs-rfid-badge-order.order-number',
  rfidBadgeVisualNumbers:
    'delmonicos-shared-sdk.outputs-rfid-badge-order.rfid-badge-visual-numbers',
  quantity: 'delmonicos-shared-sdk.outputs-rfid-badge-order.quantity',
  status: 'delmonicos-shared-sdk.outputs-rfid-badge-order.status',
  totalTTC: 'delmonicos-shared-sdk.outputs-rfid-badge-order.total-ttc',
  confirmedAt: 'delmonicos-shared-sdk.outputs-rfid-badge-order.confirmed-at',
  paidAt: 'delmonicos-shared-sdk.outputs-rfid-badge-order.paid-at',
  shippedAt: 'delmonicos-shared-sdk.outputs-rfid-badge-order.shipped-at',
  billingAddress:
    'delmonicos-shared-sdk.outputs-rfid-badge-order.billing-address',
};
